import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { circuitApi, bodiesOfWaterApi } from '../utils/api';

export default function CircuitStandings() {
  const [standings, setStandings] = useState([]);
  const [regions, setRegions] = useState([]);
  const [stats, setStats] = useState(null);
  const [bodiesOfWater, setBodiesOfWater] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedRegion, setSelectedRegion] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    bodiesOfWaterApi.listPublic()
      .then(r => { if (r.data.success) setBodiesOfWater(r.data.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedRegion]);

  async function fetchData() {
    setLoading(true);
    try {
      const params = { year: selectedYear };
      if (selectedRegion) params.region = selectedRegion;

      const [standingsRes, regionsRes, statsRes] = await Promise.all([
        circuitApi.getStandings(params),
        circuitApi.getRegions({ year: selectedYear }),
        circuitApi.getStats(params)
      ]);

      setStandings(standingsRes.data.data.standings);
      setRegions(regionsRes.data.data.regions);
      setStats(statsRes.data.data);
    } catch (err) {
      console.error('Fetch circuit data error:', err);
    } finally {
      setLoading(false);
    }
  }

  // Generate array of available years (current year only)
  const availableYears = [new Date().getFullYear()];

  if (loading) {
    return (
      <div className="page">
        <div className="page-content">
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-secondary)' }}>
            <div className="spinner" style={{ margin: '0 auto 20px' }} />
            Loading circuit standings...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-content">
        <div className="page-header">
          <h1>Standings</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Year-long team rankings across all tournaments</p>
        </div>

        {/* Filters */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: '0 0 auto' }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Season</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{ minWidth: 120 }}
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div style={{ flex: '0 0 auto' }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Region</label>
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                style={{ minWidth: 200 }}
              >
                <option value="">All Regions</option>
                {bodiesOfWater.length > 0
                  ? bodiesOfWater.map(b => (
                      <option key={b.id} value={b.name}>{b.name}</option>
                    ))
                  : regions.map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))
                }
              </select>
            </div>

            <div style={{ flex: 1, minWidth: 200, padding: '16px 20px', background: 'var(--surface)', borderRadius: 8 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 4 }}>Season Stats</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                <strong>{stats?.total_teams || 0}</strong> teams &middot; <strong>{stats?.total_events || 0}</strong> events &middot; <strong>{stats?.total_results || 0}</strong> results
              </div>
            </div>
          </div>
        </div>

        {/* Standings Table */}
        {standings.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h3 style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>No circuit data yet for {selectedYear}</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Circuit standings will appear as tournaments are completed.
            </p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--surface)', borderBottom: '2px solid var(--border)' }}>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>RANK</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>TEAM</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>POINTS</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>EVENTS</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>BEST</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>AVG</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>PODIUMS</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((team, idx) => {
                    const isTopThree = team.rank <= 3;
                    const rankColor = team.rank === 1 ? 'var(--amber)' : team.rank === 2 ? '#c0c0c0' : team.rank === 3 ? '#cd7f32' : 'var(--text-primary)';

                    return (
                      <tr
                        key={team.id}
                        style={{
                          background: isTopThree ? 'rgba(232,168,56,0.08)' : idx % 2 === 0 ? 'var(--navy-card)' : 'transparent',
                          borderBottom: '1px solid var(--border)',
                        }}
                      >
                        {/* Rank */}
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ fontSize: '1.3rem', fontWeight: 700, color: rankColor }}>
                            {team.rank}
                          </span>
                        </td>

                        {/* Team Name */}
                        <td style={{ padding: '16px' }}>
                          <Link
                            to={`/circuit/teams/${team.team_id}?year=${selectedYear}&region=${selectedRegion}`}
                            style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none' }}
                          >
                            {team.team_name}
                          </Link>
                          {team.captain_name && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                              Capt: {team.captain_name}
                            </div>
                          )}
                        </td>

                        {/* Circuit Points */}
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--amber)' }}>
                            {parseFloat(team.total_circuit_points).toFixed(1)}
                          </span>
                        </td>

                        {/* Events Entered */}
                        <td style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                          {team.tournaments_entered}
                        </td>

                        {/* Best Finish */}
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 600, color: team.best_finish === 1 ? 'var(--amber)' : 'var(--text-primary)' }}>
                            {ordinal(team.best_finish)}
                          </span>
                        </td>

                        {/* Average Finish */}
                        <td style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                          {parseFloat(team.avg_finish).toFixed(1)}
                        </td>

                        {/* Podiums */}
                        <td style={{ padding: '16px', textAlign: 'center', fontSize: '0.85rem' }}>
                          <span style={{ color: 'var(--amber)' }}>{team.first_place_count || 0}</span>
                          {' '}
                          <span style={{ color: '#c0c0c0' }}>{team.second_place_count || 0}</span>
                          {' '}
                          <span style={{ color: '#cd7f32' }}>{team.third_place_count || 0}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* How it works */}
        <div className="card" style={{ marginTop: 24, background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <h4 style={{ marginBottom: 12, fontSize: '0.9rem', color: 'var(--amber)' }}>How Circuit Points Work</h4>
          <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--text-secondary)', fontSize: '0.8rem', lineHeight: 1.8 }}>
            <li><strong>Placement Points:</strong> 1st = 100, 2nd = 90, 3rd = 85, scaling down to 12 points for 21st+</li>
            <li><strong>Field Multiplier:</strong> Larger tournaments (more teams) give bonus points via multiplier</li>
            <li><strong>Participation Bonus:</strong> +5 points per event, capped at 25 points max (5 events)</li>
            <li><strong>Total = (Base Points x Field Multiplier) + Participation Bonus</strong></li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// Helper: Convert number to ordinal (1st, 2nd, 3rd, etc.)
function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
